/**
 * A lightweight `Set`-compatible container that delegates `has()` lookups to a
 * parent Set/ScopedSet, but confines `add()` writes to its own-local storage.
 *
 * This mirrors JavaScript prototype-chain semantics:
 * - Values added to the parent are **visible** to the child via `has()`.
 * - Values added to the child are **isolated** from the parent;
 *   the parent Set is never modified by child `add()` calls.
 *
 * @example
 * const global = new Set(['a']);
 * const sandbox = new ScopedSet(global);
 * sandbox.has('a');   // true  (inherited from parent)
 * sandbox.add('b');
 * sandbox.has('b');   // true  (own-local)
 * global.has('b');    // false (never written to parent)
 */
export class ScopedSet {
    #own = new Set();
    #parent;
    constructor(parent) {
        this.#parent = parent;
    }
    /** `true` if value is in own-local storage OR anywhere in the parent chain. */
    has(value) {
        return this.#own.has(value) || (this.#parent?.has(value) ?? false);
    }
    /** Adds to own-local storage only — never propagates to the parent. */
    add(value) {
        this.#own.add(value);
        return this;
    }
    /** Removes from own-local storage only. */
    delete(value) {
        return this.#own.delete(value);
    }
    /** Clears own-local storage only. */
    clear() {
        this.#own.clear();
    }
    /** Number of own-local entries (does not include parent entries). */
    get size() { return this.#own.size; }
    forEach(cb) { this.#own.forEach(cb); }
    values() { return this.#own.values(); }
    keys() { return this.#own.keys(); }
    entries() { return this.#own.entries(); }
    [Symbol.iterator]() { return this.#own[Symbol.iterator](); }
    get [Symbol.toStringTag]() { return 'ScopedSet'; }
}
